import {
  ChapterInfo,
  ErgRow,
  MatrixCellData,
  MetricData,
  OmniMetricDefinition,
  OmniVisibleRow,
  ResolvedCellDisplay,
  SiteColumn,
  SUMMARY_VALUE_NA,
  SummaryEntry,
} from '../omnidashboard.models';
import { cellKey } from '../omnidashboard.config';
import {
  buildDefaultMetricMap,
  getMetricDefinitionOrThrow,
  OMNI_METRIC_DEFINITIONS,
} from '../metrics/metric-registry';

const CHAPTER_STATUS_METRIC = getMetricDefinitionOrThrow('chapter-status');
const CHAPTER_STATUS_BUCKET = CHAPTER_STATUS_METRIC.summaryBuckets[0];
const DEFAULT_METRIC_ENTRIES = Array.from(buildDefaultMetricMap(OMNI_METRIC_DEFINITIONS).entries());

function createDefaultMetricMap(): Map<string, MetricData> {
  return new Map<string, MetricData>(DEFAULT_METRIC_ENTRIES);
}

export function deriveAxesFromChapters(chapters: ChapterInfo[]): { siteColumns: SiteColumn[]; ergRows: ErgRow[] } {
  const siteSet = new Set<string>();
  const ergSet = new Set<string>();
  chapters.forEach((ch) => {
    siteSet.add(ch.siteName);
    ergSet.add(ch.bpgName);
  });
  return {
    siteColumns: Array.from(siteSet).sort(function sortSite(a, b) { return a.localeCompare(b); }).map(siteName => ({ siteName })),
    ergRows: Array.from(ergSet).sort(function sortErg(a, b) { return a.localeCompare(b); }).map(bpgName => ({ bpgName })),
  };
}

export function buildMatrixCellMap(chapters: ChapterInfo[]): Map<string, MatrixCellData> {
  const cellMap = new Map<string, MatrixCellData>();
  chapters.forEach((ch) => {
    const key = cellKey(ch.siteName, ch.bpgName);
    const metricMap = createDefaultMetricMap();
    // chapter-status does not use a loader; seed active cells when the matrix is built.
    metricMap.set(CHAPTER_STATUS_METRIC.key, {
      displayValue: CHAPTER_STATUS_BUCKET ? CHAPTER_STATUS_BUCKET.iconClass : '',
      color: CHAPTER_STATUS_BUCKET ? CHAPTER_STATUS_BUCKET.color : CHAPTER_STATUS_METRIC.naMetric.color,
      value: CHAPTER_STATUS_BUCKET ? CHAPTER_STATUS_BUCKET.value : 'Active',
    });
    cellMap.set(key, {
      chapterId: ch.chapterId,
      siteName: ch.siteName,
      bpgName: ch.bpgName,
      metrics: metricMap,
    });
  });
  return cellMap;
}

export function getCellData(
  matrixCellMap: Map<string, MatrixCellData>,
  siteName: string,
  bpgName: string
): MatrixCellData {
  return matrixCellMap.get(cellKey(siteName, bpgName))
    || { chapterId: 0, siteName: siteName, bpgName: bpgName, metrics: createDefaultMetricMap() };
}

export function buildMatrixGrid(
  ergRows: ErgRow[],
  siteColumns: SiteColumn[],
  matrixCellMap: Map<string, MatrixCellData>
): MatrixCellData[][] {
  return ergRows.map(erg => siteColumns.map(site => getCellData(matrixCellMap, site.siteName, erg.bpgName)));
}

export function buildResolvedGrid(
  matrixGrid: MatrixCellData[][],
  metric: OmniMetricDefinition,
  tooltipResolver: (cell: MatrixCellData) => string
): ResolvedCellDisplay[][] {
  return matrixGrid.map(row =>
    row.map((cell) => {
      const resolvedMetric = cell.metrics.get(metric.key) || metric.naMetric;
      return {
        bg: resolvedMetric.color.bg,
        text: resolvedMetric.color.text,
        displayValue: resolvedMetric.displayValue,
        tooltip: tooltipResolver(cell),
      };
    })
  );
}

export function buildVisibleRows(
  ergRows: ErgRow[],
  siteColumns: SiteColumn[],
  visibleErgRows: ErgRow[],
  visibleSiteColumns: SiteColumn[],
  matrixGrid: MatrixCellData[][],
  resolvedGrid: ResolvedCellDisplay[][],
  matrixCellMap: Map<string, MatrixCellData>,
  metric: OmniMetricDefinition,
  tooltipResolver: (cell: MatrixCellData) => string
): OmniVisibleRow[] {
  const rowIndexByBpg = new Map<string, number>(
    ergRows.map((erg, index) => [erg.bpgName, index])
  );
  const colIndexBySite = new Map<string, number>(
    siteColumns.map((site, index) => [site.siteName, index])
  );

  return visibleErgRows.map((erg) => {
    const rowIndex = rowIndexByBpg.get(erg.bpgName);
    const matrixRow = rowIndex != null ? (matrixGrid[rowIndex] || []) : [];
    const resolvedRow = rowIndex != null ? (resolvedGrid[rowIndex] || []) : [];

    const cells = visibleSiteColumns.map((site) => {
      const colIndex = colIndexBySite.get(site.siteName);
      const cell = colIndex != null && matrixRow[colIndex]
        ? matrixRow[colIndex]
        : getCellData(matrixCellMap, site.siteName, erg.bpgName);

      const resolved = colIndex != null && resolvedRow[colIndex]
        ? resolvedRow[colIndex]
        : resolveCellDisplay(cell, metric, tooltipResolver);
      return {
        key: cellKey(site.siteName, erg.bpgName),
        site: site,
        cell: cell,
        resolved: resolved,
      };
    });

    return { erg: erg, cells: cells };
  });
}

export function countVisibleChapters(visibleRows: OmniVisibleRow[]): number {
  let chapterCount = 0;
  visibleRows.forEach((row) => {
    row.cells.forEach((item) => {
      if (item.cell.chapterId) {
        chapterCount += 1;
      }
    });
  });
  return chapterCount;
}

export function buildSummary(
  metric: OmniMetricDefinition,
  visibleRows: OmniVisibleRow[],
  visibleErgRows: ErgRow[],
  visibleSiteColumns: SiteColumn[]
): SummaryEntry[] {
  if (!visibleErgRows.length || !visibleSiteColumns.length) {
    return [];
  }

  const visibleSlotCount = visibleErgRows.length * visibleSiteColumns.length;

  const counts = new Map<string, number>();
  metric.summaryBuckets.forEach((bucket) => counts.set(bucket.value, 0));

  visibleRows.forEach((row) => {
    row.cells.forEach((item) => {
      if (!item.cell.chapterId) {
        return;
      }
      const currentMetric = item.cell.metrics.get(metric.key) || metric.naMetric;
      const val = currentMetric.value;
      if (val && counts.has(val)) {
        counts.set(val, (counts.get(val) || 0) + 1);
      }
    });
  });

  const summary = metric.summaryBuckets.map((bucket) => ({
    value: bucket.value,
    label: bucket.label,
    count: counts.get(bucket.value) || 0,
    bg: bucket.color.bg,
    text: bucket.color.text,
    tooltip: bucket.tooltip,
    iconClass: bucket.iconClass,
  }));

  const total = summary.reduce((acc, entry) => acc + entry.count, 0);
  const naCount = Math.max(0, visibleSlotCount - total);
  if (naCount > 0) {
    // Include empty ERG×site slots in the summary as N/A.
    summary.push({
      value: SUMMARY_VALUE_NA,
      label: 'N/A',
      count: naCount,
      bg: metric.naMetric.color.bg,
      text: metric.naMetric.color.text,
      tooltip: 'No chapter exists for this ERG at this site',
      iconClass: 'fa-ban',
    });
  }
  return summary;
}

export function resolveCellDisplay(
  cell: MatrixCellData,
  metric: OmniMetricDefinition,
  tooltipResolver: (cell: MatrixCellData) => string
): ResolvedCellDisplay {
  const resolvedMetric = cell.metrics.get(metric.key) || metric.naMetric;
  return {
    bg: resolvedMetric.color.bg,
    text: resolvedMetric.color.text,
    displayValue: resolvedMetric.displayValue,
    tooltip: tooltipResolver(cell),
  };
}
